import React, {useEffect} from 'react';
import Box from "@mui/material/Box";
import 'moment/locale/en-gb';
import TextField from "@mui/material/TextField";
import {Autocomplete, Slider, Stack, Switch} from "@mui/material";
import Typography from "@mui/material/Typography";
import FormControlLabel from "@mui/material/FormControlLabel";
import {AdapterMoment} from "@mui/x-date-pickers/AdapterMoment";
import {DatePicker, LocalizationProvider} from "@mui/x-date-pickers";
import moment from "moment-timezone";
import {useLazyGetUnpagedAirfieldsQuery} from "../../slices/apiSlice";
import {seasonSetupConfig} from './AddEvent';
import {countryArray} from "../../util/standards-util";

const countryShortlist=['DE','GB','FR','PL','ES','IT','BA','CL','US','AU','NZ','ZA','SE','LT','HU','SI','SK']

const getFirstSaturdayFrom = (d,offsetWeeks) => {
    let date = moment().add(6, 'months').add(offsetWeeks,'weeks')

    while (date.day() !== 6) {
        date.add(1, 'days');
    }

    return date
}

const AddEventWidget = ({ isFinal = false, no, updateEventData, singleMode = false, isNonSeries = false}) => {

    const defaultStartDate = getFirstSaturdayFrom(new Date(),no*2)

    const [country, setCountry] = React.useState()
    const [startDate, setStartDate] = React.useState(defaultStartDate)
    const [endDate, setEndDate] = React.useState(getFirstSaturdayFrom(new Date(),no*2).add(seasonSetupConfig.defaultCompetitionDays-1,'days'))
    const [practiceDays, setPracticeDays] = React.useState(seasonSetupConfig.defaultPracticeDays)
    const [competitionDays, setCompetitionDays] = React.useState(seasonSetupConfig.defaultCompetitionDays)
    const [countryAirfields, setCountryAirfields] = React.useState([])
    const [airfield, setAirfield] = React.useState()
    const [venue, setVenue] = React.useState()
    const [editionTitle, setEditionTitle] = React.useState()
    const [fullEditionTitle, setFullEditionTitle] = React.useState()
    const [handicapped, setHandicapped] = React.useState(false)

    const [getAirfields, airfieldsResult] =
        useLazyGetUnpagedAirfieldsQuery()


    const handleCountryChange = (e, value) => {
        const country = value?.key
        setCountry(country)
        updateEventData(no,'country',country)
        getAirfields({country:country})
    }

    const handleVenueChange = (e,value) => {
        const v = e.target.value
        setVenue(v)
        updateEventData(no,'venue',v)
    }

    const handleTitleChange = (e,value) => {
        const v = e.target.value
        setEditionTitle(v)
        updateEventData(no,'editionTitle',v)
    }

    const handleFullTitleChange = (e,value) => {
        const v = e.target.value
        setFullEditionTitle(v)
        updateEventData(no,'fullEditionTitle',v)
    }

    const handleAirfieldChange = (e, value) => {
        const afd = value?.airfield
        setAirfield(afd)
        updateEventData(no,'airfield',afd)

        if (venue === undefined) {
            const v = afd?.n ?? ''
            setVenue(v)
            updateEventData(no,'venue',v)
        }
    }

    const handleStartDateChange = (m) => {
        const d = m.format('YYYY-MM-DD')
        setStartDate(d)
        updateEventData(no,'startDate',d)
    }

    const handlePracticeDaysChanged = (event, newValue) => {
        setPracticeDays(newValue)
        updateEventData(no,'practiceDays',newValue)
    }

    const handleCompetitionDaysChanged = (event, newValue) => {
        setCompetitionDays(newValue)
        updateEventData(no,'competitionDays',newValue)

    }

    const handleHandicappedChange = (event, newValue) => {
        setHandicapped(newValue)
        updateEventData(no,'handicapped',newValue)

    }

    useEffect(()=>{
        updateEventData(no,'startDate',defaultStartDate.format('YYYY-MM-DD'))
    },[])

    useEffect(()=>{
        if (airfieldsResult.isSuccess) {
            const data = airfieldsResult.data.map( a => {return {key:a.i,label:a.n, airfield: a}})
            setCountryAirfields(data)
        }
    },[airfieldsResult])

    useEffect(()=>{
        if (startDate) {

            setEndDate(moment(startDate).add(competitionDays-1,'day'))
        }
    },[startDate,competitionDays])

    return (
        <Box sx={{background: no % 2 === 0 ? '#ffffff' : '#eeeeee'}} p={2}>
            { isNonSeries &&
                <Box sx={{width:'28em'}}>
                    <Stack alignItems='left' spacing={2} mb={2}>
                        <TextField variant='outlined' id="fullEditionTitle" name="fullEditionTitle" label="Event title" value={fullEditionTitle} InputLabelProps={{ shrink: ( fullEditionTitle!=='' && fullEditionTitle!==undefined) }}  onChange={handleFullTitleChange} sx={{minWidth:'25em',background:'#ffffff'}} />
                        <TextField variant='outlined' id="editionTitle" name="editionTitle" label="Short event title" value={editionTitle} InputLabelProps={{ shrink: ( editionTitle!=='' && editionTitle!==undefined) }}  onChange={handleTitleChange} sx={{minWidth:'18em',background:'#ffffff'}} />
                    </Stack>
                </Box>
            }
            { !isNonSeries && <Typography variant={'body1'}  sx={{textAlign:'left',mb:1}}>{isFinal ? 'Final' : `Qualifier ${singleMode? '' : no+1}`}</Typography>}
            <Stack direction={'row'} alignItems='center' spacing={2}>
                <Autocomplete
                    disablePortal
                    selectOnFocus
                    clearOnBlur
                    handleHomeEndKeys
                    id="country"
                    name="country"
                    options={countryArray}
                    renderInput={(params) => <TextField {...params} required label="Country"  sx={{width:'18em',background:'#ffffff'}}/>}
                    onChange={handleCountryChange}
                />
                <LocalizationProvider dateAdapter={AdapterMoment} adapterLocale={true ? "en-gb" : 'en'}>
                    <DatePicker id='startDate' name="startDate" label="Competition start date" onChange={handleStartDateChange} sx={{background:'#ffffff',minWidth:'15em'}} defaultValue={getFirstSaturdayFrom(new Date(),no*2)}/>
                </LocalizationProvider>
                <Box pl={2}>
                    <Stack direction={'row'} alignItems='center' spacing={2}>
                        <Slider
                            defaultValue={seasonSetupConfig.defaultPracticeDays}
                            step={1}
                            marks
                            sx={{width:'5em'}}
                            min={1}
                            max={4}
                            onChange={handlePracticeDaysChanged}
                        />
                        <Typography variant={'body1'}>{practiceDays} practice days</Typography>
                    </Stack>
                    <Stack direction={'row'} alignItems='center' spacing={2}>
                        <Slider
                            defaultValue={seasonSetupConfig.defaultCompetitionDays}
                            step={1}
                            marks
                            sx={{width:'5em'}}
                            min={6}
                            max={9}
                            onChange={handleCompetitionDaysChanged}
                        />
                        <Typography variant={'body1'}>{competitionDays} competition days</Typography>
                    </Stack>
                </Box>
                <LocalizationProvider dateAdapter={AdapterMoment} adapterLocale={true ? "en-gb" : 'en'}>
                    <DatePicker id='endDate' name="endDate" label="Competition end date" value={endDate} disabled sx={{background:'#ffffff'}}/>
                </LocalizationProvider>

            </Stack>
            <Stack direction={'row'} alignItems='center' spacing={2} mt={2}>
                <Autocomplete
                    disablePortal
                    selectOnFocus
                    clearOnBlur
                    handleHomeEndKeys
                    id="airfield"
                    name="airfield"
                    options={countryAirfields}
                    renderInput={(params) => <TextField {...params} label="Airfield"  sx={{width:'18em',background:'#ffffff'}}/>}
                    onChange={handleAirfieldChange}
                    sx={{background:'#ffffff'}}
                />
                <TextField variant='outlined' id="venue" name="venue" label="Venue" value={venue} InputLabelProps={{ shrink: ( venue!=='' && venue!==undefined) }}  onChange={handleVenueChange} sx={{minWidth:'15em',background:'#ffffff'}} />
                <FormControlLabel sx={{visibility:isFinal ? 'hidden' : 'visible'}} control={<Switch name={'handicapped'} onChange={handleHandicappedChange}/>} label={<Typography variant={'body2'}>Handicapped</Typography>}/>

            </Stack>
        </Box>
    )
}

export default AddEventWidget;